import React, { useEffect } from 'react';
import { Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { WEBSITE_CONTENT_DELETED_LIST } from '../graphql/queries';
import { WebsiteDeletedItem, WebsiteDeletedList } from '../graphql/types';
import { resolveWebsiteContentError } from '../utils/errorMessage';
import { formatLocalDateTime } from '../utils/format';
import ContentCover from '../components/ContentCover';

const PAGE_SIZE = 20;

/** 已删除内容（T-A04）：只读，按删除时间倒序；不提供查看正文与恢复 */
const WebsiteContentDeletedPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '', 10) || 1);

    const { data, previousData, loading, error } = useQuery<{ websiteContentDeletedList: WebsiteDeletedList }>(
        WEBSITE_CONTENT_DELETED_LIST,
        { variables: { page, pageSize: PAGE_SIZE, lang: 'EN' }, fetchPolicy: 'cache-and-network' },
    );
    const list = (data ?? previousData)?.websiteContentDeletedList;

    useEffect(() => {
        if (!error) return;
        const text = resolveWebsiteContentError(error, t);
        if (text) message.error(text);
    }, [error, t]);

    const columns: ColumnsType<WebsiteDeletedItem> = [
        {
            title: t('websiteContent.list.columns.cover'),
            key: 'cover',
            width: 96,
            render: (_, item) => <ContentCover url={item.coverThumbnailUrl} />,
        },
        {
            title: t('websiteContent.list.columns.title'),
            key: 'title',
            render: (_, item) => (
                <div>
                    <div className="font-medium">
                        {item.title || <span className="text-gray-400">{t('websiteContent.list.untitled')}</span>}
                    </div>
                    <div className="text-xs text-gray-400 font-mono mt-1">{item.slug}</div>
                </div>
            ),
        },
        {
            title: t('websiteContent.deleted.columns.statusBeforeDelete'),
            key: 'status',
            width: 140,
            render: (_, item) => <Tag>{t(`websiteContent.status.${item.status}`)}</Tag>,
        },
        {
            title: t('websiteContent.deleted.columns.deletedBy'),
            key: 'deletedBy',
            width: 160,
            render: (_, item) => item.deletedByName ?? '-',
        },
        {
            title: t('websiteContent.deleted.columns.deletedAt'),
            key: 'deletedAt',
            width: 180,
            render: (_, item) => formatLocalDateTime(item.deletedAt),
        },
    ];

    return (
        <div className="flex flex-col gap-4 min-h-0">
            <div>
                <Typography.Title level={4} className="!mb-1">{t('websiteContent.deleted.title')}</Typography.Title>
                <Typography.Text type="secondary">{t('websiteContent.deleted.hint')}</Typography.Text>
            </div>
            <Table<WebsiteDeletedItem>
                rowKey="id"
                columns={columns}
                dataSource={list?.items ?? []}
                loading={loading}
                locale={{ emptyText: t('websiteContent.deleted.empty') }}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: list?.total ?? 0,
                    showSizeChanger: false,
                    showTotal: (total) => t('websiteContent.list.total', { total }),
                    onChange: (next) => setSearchParams(next > 1 ? { page: String(next) } : {}),
                }}
            />
        </div>
    );
};

export default WebsiteContentDeletedPage;
